import React, { useState } from 'react';
import {
  Table, Tag, Button, Modal, Form, Select, Input, message, Descriptions, Empty, Alert, Space,
} from 'antd';
import { useListUsersQuery, useUpdateUserMutation } from '../services/adminApi';
import AdminPageHeader, { adminPanelStyle } from './AdminPageHeader';

const ROLE_COLORS = {
  admin: 'red',
  moderator: 'orange',
  contributor: 'purple',
  member: 'blue',
};

const ROLE_OPTIONS = [
  { value: 'admin', label: 'Admin' },
  { value: 'moderator', label: 'Moderator' },
  { value: 'contributor', label: 'Contributor' },
  { value: 'member', label: 'Member' },
];

export default function UsersPage() {
  const [roleFilter, setRoleFilter] = useState(undefined);
  const [search, setSearch] = useState('');
  const { data, isLoading, isError, error } = useListUsersQuery({
    role: roleFilter,
    search: search || undefined,
  });
  const [updateUser] = useUpdateUserMutation();
  const [modalOpen, setModalOpen] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [form] = Form.useForm();
  const [saving, setSaving] = useState(false);

  const handleOpen = (user) => {
    setSelectedUser(user);
    form.setFieldsValue({ role: user.role, display_name: user.display_name });
    setModalOpen(true);
  };

  const handleSave = async (values) => {
    setSaving(true);
    try {
      await updateUser({ userId: selectedUser.id, ...values }).unwrap();
      message.success('User updated');
      setModalOpen(false);
    } catch (err) {
      message.error(err.data?.title || 'Failed to update user');
    } finally {
      setSaving(false);
    }
  };

  const columns = [
    { title: 'ID', dataIndex: 'id', key: 'id', width: 70 },
    { title: 'Email', dataIndex: 'email', key: 'email' },
    { title: 'Display Name', dataIndex: 'display_name', key: 'display_name' },
    {
      title: 'Role',
      dataIndex: 'role',
      key: 'role',
      width: 120,
      render: (role) => <Tag color={ROLE_COLORS[role]}>{role}</Tag>,
    },
    {
      title: 'Verified',
      dataIndex: 'email_verified_at',
      key: 'verified',
      width: 100,
      render: (v) => (v ? <Tag color="green">Yes</Tag> : <Tag color="red">No</Tag>),
    },
    { title: 'Reputation', dataIndex: 'reputation_score', key: 'reputation', width: 100 },
    {
      title: 'Created',
      dataIndex: 'created_at',
      key: 'created_at',
      width: 170,
      render: (d) => (d ? new Date(d).toLocaleString() : '—'),
    },
    {
      title: 'Actions',
      key: 'actions',
      width: 100,
      render: (_, record) => (
        <Button size="small" onClick={() => handleOpen(record)}>Edit</Button>
      ),
    },
  ];

  return (
    <div>
      <AdminPageHeader
        title="Users"
        subtitle="Manage roles and display names"
        extra={(
          <Space wrap>
            <Input.Search
              allowClear
              placeholder="Search email or name"
              onSearch={setSearch}
              style={{ width: 220 }}
            />
            <Select
              allowClear
              placeholder="Filter role"
              style={{ width: 160 }}
              options={ROLE_OPTIONS}
              value={roleFilter}
              onChange={setRoleFilter}
            />
          </Space>
        )}
      />

      {isError && (
        <Alert
          type="error"
          showIcon
          style={{ marginBottom: 16 }}
          message={error?.data?.title || 'Failed to load users'}
        />
      )}

      <div style={adminPanelStyle}>
        <Table
          dataSource={data?.items}
          columns={columns}
          loading={isLoading}
          rowKey="id"
          pagination={{ pageSize: 20 }}
          size="middle"
          locale={{ emptyText: <Empty description="No users found" /> }}
        />
      </div>

      <Modal
        title="Edit User"
        open={modalOpen}
        onCancel={() => setModalOpen(false)}
        onOk={() => form.submit()}
        confirmLoading={saving}
      >
        {selectedUser && (
          <>
            <Descriptions size="small" column={1} style={{ marginBottom: 16 }}>
              <Descriptions.Item label="Email">{selectedUser.email}</Descriptions.Item>
              <Descriptions.Item label="Public ID">{selectedUser.public_id}</Descriptions.Item>
              <Descriptions.Item label="Joined">
                {new Date(selectedUser.created_at).toLocaleDateString()}
              </Descriptions.Item>
            </Descriptions>
            <Form form={form} layout="vertical" onFinish={handleSave}>
              <Form.Item name="role" label="Role" rules={[{ required: true }]}>
                <Select options={ROLE_OPTIONS} />
              </Form.Item>
              <Form.Item name="display_name" label="Display Name">
                <Input />
              </Form.Item>
            </Form>
          </>
        )}
      </Modal>
    </div>
  );
}
